import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { api } from '../lib/api-client';

export interface LegalClient {
  id: string;
  name: string;
  logo_url?: string | null;
  domain?: string | null;
  primary_contact?: string | null;
  primary_email?: string | null;
  status: 'active' | 'inactive';
}

interface ClientContextValue {
  selectedClient: LegalClient | null;
  clients: LegalClient[];
  clientsLoading: boolean;
  selectClient: (client: LegalClient) => void;
  clearClient: () => void;
  refreshClients: () => Promise<void>;
}

const ClientContext = createContext<ClientContextValue | null>(null);

const STORAGE_KEY = 'legal_selected_client';

export function ClientProvider({ children }: { children: ReactNode }) {
  const [selectedClient, setSelectedClient] = useState<LegalClient | null>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) as LegalClient : null;
    } catch {
      return null;
    }
  });
  const [clients, setClients] = useState<LegalClient[]>([]);
  const [clientsLoading, setClientsLoading] = useState(false);

  const refreshClients = useCallback(async () => {
    setClientsLoading(true);
    try {
      const res = await api.get<{ clients: LegalClient[] }>('/api/v1/legal/clients');
      setClients(res.clients ?? []);
    } catch {
      setClients([]);
    } finally {
      setClientsLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshClients();
  }, [refreshClients]);

  const selectClient = useCallback((client: LegalClient) => {
    setSelectedClient(client);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(client));
  }, []);

  const clearClient = useCallback(() => {
    setSelectedClient(null);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  return (
    <ClientContext.Provider
      value={{
        selectedClient,
        clients,
        clientsLoading,
        selectClient,
        clearClient,
        refreshClients,
      }}
    >
      {children}
    </ClientContext.Provider>
  );
}

export function useClientContext(): ClientContextValue {
  const ctx = useContext(ClientContext);
  if (!ctx) throw new Error('useClientContext must be used within ClientProvider');
  return ctx;
}

/** Returns `?company_id=X` query string when a client is selected, or empty string. */
export function useClientFilter(): string {
  const { selectedClient } = useClientContext();
  return selectedClient ? `?company_id=${selectedClient.id}` : '';
}
